import { TimezonedDate } from './timezoned-date';

/**
 * /////////////////////////////////////////
 * API DTO types
 *
 * The following types are used only for API requests.
 */

export interface TilesFilterType {
    1: number[] | undefined;
    2: number[] | undefined;
    3: number[] | undefined;
}

export interface GroupFilterType {
    id: number;
    name: string;
}

// TODO: This is the same type as ActivityFiltersForm in the backend
// TODO: Fix date | string
// TODO Backend type definition and frontend usage differ, many fields in the frontend are optional, but mandatory in the backend
export interface ActivityFiltersForm {
    note?: string | boolean | undefined;
    startDate: Date | string; // backend type: Date
    endDate: Date | string; // backend type: Date
    strictDate?: boolean;
    userIds: number[] | undefined;
    tiles?: TilesFilterType; // backend type: Map<number, number[]>
    billed?: boolean | undefined;
    // runningActivities: boolean;   //unused
    // customData?: string;          //supported, but not used yet.
    groups?: GroupFilterType[]; // backend type: Group[]    TODO this should be a number array like userIds
}

export interface ActivityFiltersFormPageHack extends ActivityFiltersForm {
    pageNumber?: number;
    pageSize?: number;
}

// from backend:
export enum LockReasonCode {
    BILLED_POLICY = 'BILLED_POLICY',
    RETROACTIVE_EDIT_LIMIT_POLICY = 'RETROACTIVE_EDIT_LIMIT_POLICY',
}

// from backend:
export type LockState = {
    locked: boolean;
    reasonCodes: LockReasonCode[];
    userCanOverride: boolean;
};

export interface ActivityDto {
    id: number;
    startDate: string;
    startUtcOffset: string;
    endUtcOffset: string;
    endDate: string;
    tiles: number[];
    note: string;
    billed: boolean;
    userId: number;
    customData: string | null;
    locked?: boolean; // computed, read-only field
    lock?: LockState; // computed, read-only field
}

export type ActivityToCreateDto = Omit<ActivityDto, 'id'>;

export interface FilteredActivitiesResponseDto {
    activities: ActivityDto[];
    totalCount: number;
    totalDuration: number;
}

// return type for meta data request
export interface ActivitiesMetadataForm {
    totalCount: number;
    lastPage: number;
    lastPageSize: number;
    totalDuration: number;
    roundedDuration: number;
    idList: string;
}

// from backend:
export interface TileViewForm {
    id: number;
    name: string;
    color: string;
    layerIndex: number;
}

/**
 * /////////////////////////////////////////
 * Activity model type
 *
 * The following type represents the Activity model
 * and is the core to be used across the application.
 */

export interface Activity {
    id: number;
    startDate: TimezonedDate; // special object for dates in different timezones
    // startUtcOffset: string;      //can be retrieved via startDate.getUtcOffset();
    endDate: TimezonedDate; // special object for dates in different timezones
    // endUtcOffset: string;        //can be retrieved via endDate.getUtcOffset();
    note: string;
    billed: boolean;
    userId: number;
    // user: UserEntity;
    tileIds: number[]; // ActivityForm.tiles (array of ids), ActivityEntity.tiles (array of related entities)
    tiles_?: TileViewForm[];
    // tiles: TileEntity[];
    customData: string | null;
    lock_?: LockState; // computed, read-only field
}

/**
 * Own type for creation of activities.
 * This type does not have an ID.
 */
export type ActivityToCreate = Omit<Activity, 'id'>;

// TODO where to put these helpers? In a factory?

/**
 * This converts a model to create to a model by adding an id field.
 * @param id The id to be added to the create model to make it a normal model
 * @param activity ActivityToCreate object without ID
 * @returns Activity object with ID
 */
export function activityModelAddId(id: number, activity: ActivityToCreate): Activity {
    return {
        id,
        ...activity,
    };
}

/**
 * When creating a new activity object, pass it in this function before further usage.
 * The function will assure, that the date and other objects are correctly set, e.g. removing millis from date.
 * @param activity
 */
export function createActivity(activity: ActivityToCreate): ActivityToCreate {
    const retVal = { ...activity };
    retVal.startDate.setMilliseconds(0);
    retVal.endDate.setMilliseconds(0);
    return retVal;
}

/**
 * /////////////////////////////////////////
 * Specific API response types with models:
 */

// TODO check, if this can be removed and be pure Activity[] as we now have the meta request to get this information!
export interface FilteredActivitiesResponse {
    activities: Activity[];
    totalCount: number;
    totalDuration: number;
}

/**
 * /////////////////////////////////////////
 * View model types
 *
 * The following types are specialized representations of the model
 * for easing usage within specific components.
 *
 * TODO: They should be part of the component and not globally here...
 *
 */

export type ActivityDetailVmKeys = keyof ActivityDetailVm; // e.g. 'foo' | 'bar'

export interface ActivityDetailVm {
    id: number;
    userId: number;
    userName: string;
    tiles: TileViewForm[];
    tileNames: string[];
    tileIds: (number | undefined)[];
    layer1: string;
    layer2?: string;
    layer3?: string;
    note: string;
    date: string;
    time: string;
    start: TimezonedDate;
    end: TimezonedDate;
    duration: string;
    durationSeconds: number;
    billed: boolean;
    isChecked: boolean;
    timezoneSwitch: boolean;
    lock?: LockState;
}

export interface ActivityDetailPdfVm {
    userName: string;
    layer1: string;
    layer2?: string;
    layer3?: string;
    note: string;
    date: string;
    time: string;
    duration: string;
    billed: string;
}

export interface DashboardActivityVM {
    time: string;
    layer1: string;
    layer2: string;
    layer3: string;
    durationSeconds: number;
    note: string;
    color: string;
}

export type ActivitySortField =
    | 'local_start_date'
    | 'local_end_date'
    | 'start_date'
    | 'end_date'
    | 'local_start_time'
    | 'layer1'
    | 'layer2'
    | 'layer3'
    | 'userName'
    | 'note'
    | 'dateForSort'
    | 'time'
    | 'durationSeconds'
    | 'billed';
